import { useRef, useState, useEffect } from 'react';
import { compressForUpload } from '../lib/compress';

interface Page {
  id: string;
  file: File;
  preview: string;
}

interface CameraCaptureProps {
  onSubmit: (files: File[]) => void;
  onCancel: () => void;
  busy?: boolean;
  errorMessage?: string | null;
  // When true, fire the file picker on first mount so the user lands directly
  // in their OS picker (Take Photo / Photo Library). Skipped on remount with
  // pages already present (e.g. add-another-page mid-flow).
  autoOpenPicker?: boolean;
}

const MAX_PAGES = 6;

export function CameraCapture({ onSubmit, onCancel, busy, errorMessage, autoOpenPicker = true }: CameraCaptureProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autoOpenedRef = useRef(false);
  const [pages, setPages] = useState<Page[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);

  useEffect(() => {
    return () => {
      pages.forEach((p) => URL.revokeObjectURL(p.preview));
    };
  }, [pages]);

  // iOS bfcache: clear file inputs on pageshow.
  useEffect(() => {
    const handler = () => {
      if (inputRef.current) inputRef.current.value = '';
    };
    window.addEventListener('pageshow', handler);
    return () => window.removeEventListener('pageshow', handler);
  }, []);

  const triggerInput = () => {
    if (!inputRef.current) return;
    inputRef.current.click();
  };

  // Auto-open the OS picker on first mount so the home → file-picker flow
  // skips the intermediate "Open camera" tap. The picker still appears on
  // user gesture (the click that mounted us is the gesture), and modern
  // browsers honor it. If the user dismisses the picker, they fall back to
  // the empty-state UI below and can re-tap.
  useEffect(() => {
    if (!autoOpenPicker) return;
    if (autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    triggerInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenPicker]);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (pages.length >= MAX_PAGES) {
      setLocalError(`Maximum ${MAX_PAGES} pages.`);
      return;
    }

    setLocalError(null);
    setCompressing(true);
    try {
      const compressed = await compressForUpload(file);
      const id = crypto.randomUUID();
      const preview = URL.createObjectURL(compressed);
      setPages((prev) => [...prev, { id, file: compressed, preview }]);
    } catch (err) {
      console.error('compress failed:', err);
      const msg = err instanceof Error && /heic|heif/i.test(err.message)
        ? 'Could not convert this HEIC photo. Try retaking it.'
        : 'Could not process this photo. Try again.';
      setLocalError(msg);
    } finally {
      setCompressing(false);
    }
  };

  const removePage = (id: string) => {
    setPages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const submit = () => {
    if (pages.length === 0 || busy) return;
    onSubmit(pages.map((p) => p.file));
  };

  const error = errorMessage ?? localError;

  return (
    <div className="min-h-dvh flex flex-col safe-top safe-bottom container-app">
      <header className="flex items-center justify-between pt-4 pb-4">
        <button
          onClick={onCancel}
          aria-label="Cancel"
          style={{ background: 'transparent', border: 'none', minWidth: 44, minHeight: 44, color: 'var(--color-primary)' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {pages.length} of {MAX_PAGES} {pages.length === 1 ? 'page' : 'pages'}
        </span>
        <span style={{ width: 44 }} />
      </header>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
        onChange={onFileChange}
        style={{ position: 'absolute', left: -9999, opacity: 0, pointerEvents: 'none' }}
      />

      {pages.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center px-6">
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              background: 'var(--color-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </div>
          <div>
            <p style={{ fontWeight: 500, fontSize: 16 }}>Capture the menu</p>
            <p className="text-sm pt-2" style={{ color: 'var(--color-text-secondary)', maxWidth: 280, margin: '0 auto', lineHeight: 1.5 }}>
              Hold steady. Fit the whole menu in the frame. You can add pages for two-page or chalkboard menus.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-3 overflow-y-auto pb-3">
          {pages.map((p, idx) => (
            <div
              key={p.id}
              className="rounded-md overflow-hidden relative"
              style={{ background: 'var(--color-surface)', minHeight: 200 }}
            >
              <img src={p.preview} alt={`Page ${idx + 1}`} style={{ width: '100%', display: 'block', maxHeight: 360, objectFit: 'contain' }} />
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  background: 'rgba(0,0,0,0.65)',
                  color: '#fff',
                  padding: '4px 10px',
                  borderRadius: 9999,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Page {idx + 1}
              </div>
              <button
                onClick={() => removePage(p.id)}
                aria-label={`Remove page ${idx + 1}`}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: 'rgba(0,0,0,0.65)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            background: 'var(--color-danger)',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: 8,
            fontSize: 14,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <div className="flex flex-col gap-1 pt-2">
        {pages.length === 0 ? (
          <button
            onClick={triggerInput}
            disabled={compressing || busy}
            className="btn-primary"
            style={{ opacity: compressing || busy ? 0.5 : 1 }}
          >
            {compressing ? 'Processing…' : 'Add photo'}
          </button>
        ) : (
          <>
            <button
              onClick={submit}
              disabled={busy || compressing}
              className="btn-primary"
              style={{ opacity: busy ? 0.5 : 1 }}
            >
              {busy ? 'Translating…' : 'Translate menu'}
            </button>

            <button
              onClick={triggerInput}
              disabled={compressing || busy || pages.length >= MAX_PAGES}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-secondary)',
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 500,
                minHeight: 40,
                opacity: pages.length >= MAX_PAGES ? 0.5 : 1,
              }}
            >
              {compressing ? 'Processing…' : '+ Add another page'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
